import { MouseEvent, useState } from 'react';
import { Box, Link } from '@mui/material';
import { GridActionsCellItemProps, GridRowParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TenantType } from 'src/types';
import { themeColors } from 'src/theme';
import CompaniesTable from 'src/components/TenantsTable';
import { Button, EmptyState, Loader, P1M, P1R, Toolbar } from 'src/components';
import { setParams } from 'src/utils';
import { PATHS } from 'src/navigation';
import { InviteAdminDialog } from 'src/containers/TenantContainer/components/';
import { DashboardWrapper } from 'src/containers/wrappers';
import StyledActionItem from 'src/components/Table/TableComponents/StyledActionItem';
import { useModal } from 'src/hooks';
import { useDeleteTenant, useTenants } from 'src/api/tenant';
import AddCompanyDialog from './components/AddCompanyDialog';

const TenantsDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [currentTenantId, setCurrentTenantId] = useState<number>();

  const { tenants, isLoading: tenantsLoading, refetch: refetchTenants } = useTenants();
  const { mutate: mutateDeleteTenant } = useDeleteTenant({
    onSuccess: () => {
      refetchTenants();
      destroy();
    },
  });

  const { confirm, context, destroy } = useModal();

  const onDeleteClick = (event: MouseEvent<HTMLElement>, tenant: TenantType) => {
    event.stopPropagation();
    confirm({
      theme: 'delete',
      title: t('companyDashboardPage.areYouSure'),
      okText: t('companyDashboardPage.yesDelete'),
      subTitle: (
        <P1R>
          {t('companyDashboardPage.aboutToDelete')}
          <P1M> {tenant.title}</P1M>
        </P1R>
      ),
      onOk: () => mutateDeleteTenant(tenant.id),
    });
  };

  const goToCompany = (id: number) => navigate(setParams(PATHS.COMPANY_PAGE, { id: id.toString() }));

  const getActions = (params: GridRowParams<any>): React.ReactElement<GridActionsCellItemProps>[] => {
    const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

    actions.push(<StyledActionItem key="editCell" label="Edit" onClick={() => goToCompany(params.row.id)} showInMenu />);

    // Delete action should be temporarily disabled
    // Link to the ticket: https://storm360.atlassian.net/browse/ERMA-3304
    actions.push(
      <StyledActionItem
        key="deleteCell"
        label="Delete"
        disabled
        onClick={(event: MouseEvent<HTMLElement>) => onDeleteClick(event, params.row)}
        color={themeColors.red}
        showInMenu
      />,
    );

    return actions;
  };

  const handleHideDialog = () => {
    setCurrentTenantId(undefined);
  };

  const onCreateSuccess = (id: number) => {
    refetchTenants();
    setCurrentTenantId(id);
  };

  return (
    <DashboardWrapper
      breadcrumbs={[[`${t('companyDashboardPage.title')} ${tenants.length ? `(${tenants.length})` : ''}`]]}
      navigationChildren={
        <NavigationWidget showToolbar={!!tenants?.length} showDialog={showDialog} setShowDialog={setShowDialog} onCreateSuccess={onCreateSuccess} />
      }
    >
      {tenantsLoading ? (
        <Loader />
      ) : tenants.length ? (
        <Box component="main" sx={{ height: 'calc(100vh - 100px)', pt: 4 }}>
          <CompaniesTable
            companies={tenants}
            getActions={getActions}
            toolbarProps={{ options: 'mini' }}
            onRowClick={(params) => goToCompany(params.id)}
          />
          {currentTenantId && <InviteAdminDialog isCompanyList onClose={handleHideDialog} currentTenantId={currentTenantId} />}
          {context}
        </Box>
      ) : (
        <EmptyState
          type="company"
          title={t('companyDashboardPage.noCompaniesTitle')}
          subTitle={
            <Link
              component="button"
              variant="body2"
              style={{
                color: themeColors.blue,
                textDecoration: 'none',
                marginLeft: '4px',
                fontSize: '14px',
              }}
              onClick={() => setShowDialog(true)}
            >
              {t('companyDashboardPage.addCompany')}
            </Link>
          }
        />
      )}
    </DashboardWrapper>
  );
};

const NavigationWidget = ({
  showToolbar,
  showDialog,
  setShowDialog,
  onCreateSuccess,
}: {
  showToolbar: boolean;
  showDialog: boolean;
  setShowDialog: (bool: boolean) => void;
  onCreateSuccess: (id: number) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box sx={{ gap: 3, display: 'flex' }}>
        {showToolbar && <Toolbar options="mini" />}
        <Button
          onClick={() => setShowDialog(true)}
          sx={{
            padding: '0 16px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {t('companyDashboardPage.addCompany')}
        </Button>
      </Box>

      <AddCompanyDialog open={showDialog} onClose={() => setShowDialog(false)} onCreateSuccess={onCreateSuccess} />
    </Box>
  );
};

export default TenantsDashboard;
